/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorStyle,
  useColorMode,
  Checkbox,
  Switch,
} from '@tonic-one/react';
import { Info } from 'src/components/Common/Info';
import { useTranslation } from 'react-i18next';
import { GlobalApi, ProvisionApi } from '@Apis';
import { ExchangeInlineProtectionReqDto } from '@Types/globalSetting';
import CSpinner from 'src/components/CSpinner';
import ErrorAlert from 'src/components/Common/ErrorAlert';
import { useOrgStore } from 'src/models/useOrgModel';
import { GrantedAccess } from '../Common/GrantedAccess';
import { PolicyType } from '../../../types/app';
import {
  ExchangeInlineProtectionSettings,
  OtherExchangeInlineProtectionSettings,
  // SenderAddressMatchSettings,
} from '../utils/types';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';

const InlineProtectionSettingsForExchangeOnline = React.memo(() => {
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [protectionSetting, setProtectionSetting] =
    useState<ExchangeInlineProtectionSettings>({
      inlineProtectionSettings: {
        inboundRuleEnable: false,
        outboundRuleEnable: false,
      },
      senderAddressMatchSettings: {
        envelopeAddressEnable: false,
        messageAddressEnable: false,
      },
      trustScanEnable: false,
    });
  // const [inboundStatus, setInboundStatus] = useState<boolean>(false);
  // const [outboundStatus, setOutboundStatus] = useState<boolean>(false);
  // const [senderAddressMatchEnable, setSenderAddressMatchEnable] =
  //   useState<SenderAddressMatchSettings>({
  //     envelopeAddressEnable: false,
  //     messageAddressEnable: false,
  //   });
  // const [trustScanEnable, setTrustScanEnable] = useState<boolean>(false);
  const { orgId } = useOrgStore();
  const [isShowMultiService, setIsShowMultiService] =
    React.useState<boolean>(false);
  const [selectedPolicyType, setSelectedPolicyType] =
    React.useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const { t } = useTranslation();

  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, [orgId]);

  const updateOtherExchangeInlineProtectionSettings = async (
    otherExchangeInlineProtectionSettings: OtherExchangeInlineProtectionSettings
  ) => {
    try {
      setLoading(true);
      await GlobalApi.setExchangeInlineOtherProtectionSettings(
        otherExchangeInlineProtectionSettings
      );
    } catch (error) {
      setErrorMsg(t('common.apiException.internalError'));
    }
    setLoading(false);
  };

  async function loadData() {
    try {
      setLoading(true);
      const policyTypes = (
        await ProvisionApi.getProvisionedServicesByOrgId(orgId)
      ).data;
      const result = policyTypes.filter((item) =>
        [PolicyType.ExchangeInline].includes(item)
      );
      if (result.length === 0) {
        setIsShowMultiService(false);
        setSelectedPolicyType(PolicyType.ExchangeInline.toString());
      } else {
        setIsShowMultiService(true);
        const inlineProtectionSettingsForExchangeOnline: ExchangeInlineProtectionSettings =
          (await GlobalApi.getExchangeInlineProtectionStatus()).data;
        setProtectionSetting(inlineProtectionSettingsForExchangeOnline);
      }
    } catch (error) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box px="4x">
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      {isShowMultiService ? (
        <Box>
          <Text
            mt="28px"
            fontSize="lg"
            lineHeight="lg"
            color={colorStyle.color.primary}
          >
            {t('globalSetting.exchangeInline.inlineProtection.title')}
          </Text>
          <Flex mt="16px" alignItems="center">
            <Switch
              disabled={!withEditPermission}
              checked={
                protectionSetting.inlineProtectionSettings.inboundRuleEnable
              }
              onChange={async (e: any) => {
                try {
                  setLoading(true);
                  setProtectionSetting({
                    inlineProtectionSettings: {
                      inboundRuleEnable: e.target.checked,
                      outboundRuleEnable:
                        protectionSetting.inlineProtectionSettings
                          .outboundRuleEnable,
                    },
                    senderAddressMatchSettings: {
                      envelopeAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .envelopeAddressEnable,
                      messageAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .messageAddressEnable,
                    },
                    trustScanEnable: protectionSetting.trustScanEnable,
                  });
                  // setInboundStatus();
                  const req: ExchangeInlineProtectionReqDto = {
                    ruleType: 'inbound',
                    enable: e.target.checked,
                  };
                  await GlobalApi.setExchangeInlineProtectionStatus(req);
                } catch (err) {
                  setErrorMsg(t('common.apiException.internalError'));
                } finally {
                  setLoading(false);
                }
              }}
            ></Switch>
            <Text ml="2x">
              {t('globalSetting.exchangeInline.inlineProtection.inbound')}
            </Text>
          </Flex>
          <Flex mt="12px" alignItems="center">
            <Switch
              disabled={!withEditPermission}
              checked={
                protectionSetting.inlineProtectionSettings.outboundRuleEnable
              }
              onChange={async (e: any) => {
                try {
                  setLoading(true);
                  // setOutboundStatus(!outboundStatus);
                  setProtectionSetting({
                    inlineProtectionSettings: {
                      inboundRuleEnable:
                        protectionSetting.inlineProtectionSettings
                          .inboundRuleEnable,
                      outboundRuleEnable: e.target.checked,
                    },
                    senderAddressMatchSettings: {
                      envelopeAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .envelopeAddressEnable,
                      messageAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .messageAddressEnable,
                    },
                    trustScanEnable: protectionSetting.trustScanEnable,
                  });
                  const req: ExchangeInlineProtectionReqDto = {
                    ruleType: 'outbound',
                    enable: e.target.checked,
                  };
                  await GlobalApi.setExchangeInlineProtectionStatus(req);
                } catch (err) {
                  setErrorMsg(t('common.apiException.internalError'));
                } finally {
                  setLoading(false);
                }
              }}
            ></Switch>
            <Text ml="2x">
              {t('globalSetting.exchangeInline.inlineProtection.outbound')}
            </Text>
          </Flex>
          <Flex mt="28px">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.exchangeInline.senderAddressMatch.title')}
            </Text>
          </Flex>
          <Flex mt="20px">
            <Text
              color={colorStyle.color.secondary}
              lineHeight="lg"
              maxWidth={1000}
            >
              {t('globalSetting.exchangeInline.senderAddressMatch.desc')}
            </Text>
          </Flex>
          <Flex m="16px 0px">
            <Checkbox
              disabled
              checked={
                protectionSetting.senderAddressMatchSettings
                  .envelopeAddressEnable
              }
            ></Checkbox>
            <Text ml="8px">
              {t(
                'globalSetting.exchangeInline.senderAddressMatch.envelope.content'
              )}
            </Text>
            <Info
              iconStyle={{ ml: '2x' }}
              content={t(
                'globalSetting.exchangeInline.senderAddressMatch.envelope.tooltip'
              )}
            />
          </Flex>
          <Flex m="16px 0px">
            <Checkbox
              disabled={!withEditPermission}
              checked={
                protectionSetting.senderAddressMatchSettings
                  .messageAddressEnable
              }
              onChange={async (e: any) => {
                try {
                  setLoading(true);
                  // setSenderAddressMatchEnable({
                  //   envelopeAddressEnable:
                  //     senderAddressMatchEnable.envelopeAddressEnable,
                  //   messageAddressEnable: e.target.checked,
                  // });
                  setProtectionSetting({
                    inlineProtectionSettings: {
                      inboundRuleEnable:
                        protectionSetting.inlineProtectionSettings
                          .inboundRuleEnable,
                      outboundRuleEnable:
                        protectionSetting.inlineProtectionSettings
                          .outboundRuleEnable,
                    },
                    senderAddressMatchSettings: {
                      envelopeAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .envelopeAddressEnable,
                      messageAddressEnable: e.target.checked,
                    },
                    trustScanEnable: protectionSetting.trustScanEnable,
                  });
                  await updateOtherExchangeInlineProtectionSettings({
                    envelopeAddressEnable:
                      protectionSetting.senderAddressMatchSettings
                        .envelopeAddressEnable,
                    messageAddressEnable: e.target.checked,
                    trustScanEnable: protectionSetting.trustScanEnable,
                  });
                } catch (err) {
                  setErrorMsg(t('common.apiException.internalError'));
                } finally {
                  setLoading(false);
                }
              }}
            ></Checkbox>
            <Text ml="8px">
              {t(
                'globalSetting.exchangeInline.senderAddressMatch.message.content'
              )}
            </Text>
            <Info
              iconStyle={{ ml: '2x' }}
              content={t(
                'globalSetting.exchangeInline.senderAddressMatch.message.tooltip'
              )}
            />
          </Flex>
          <Flex mt="32px">
            <Text
              fontSize="lg"
              lineHeight="lg"
              color={colorStyle.color.primary}
            >
              {t('globalSetting.exchangeInline.trustScan.title')}
            </Text>
          </Flex>
          <Flex m="16px 0px">
            <Checkbox
              disabled={!withEditPermission}
              checked={protectionSetting.trustScanEnable}
              onChange={async (e: any) => {
                try {
                  setLoading(true);
                  // setTrustScanEnable(e.target.checked);
                  setProtectionSetting({
                    inlineProtectionSettings: {
                      inboundRuleEnable:
                        protectionSetting.inlineProtectionSettings
                          .inboundRuleEnable,
                      outboundRuleEnable:
                        protectionSetting.inlineProtectionSettings
                          .outboundRuleEnable,
                    },
                    senderAddressMatchSettings: {
                      envelopeAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .envelopeAddressEnable,
                      messageAddressEnable:
                        protectionSetting.senderAddressMatchSettings
                          .messageAddressEnable,
                    },
                    trustScanEnable: e.target.checked,
                  });
                  await updateOtherExchangeInlineProtectionSettings({
                    envelopeAddressEnable:
                      protectionSetting.senderAddressMatchSettings
                        .envelopeAddressEnable,
                    messageAddressEnable:
                      protectionSetting.senderAddressMatchSettings
                        .messageAddressEnable,
                    trustScanEnable: e.target.checked,
                  });
                } catch (err) {
                  setErrorMsg(t('common.apiException.internalError'));
                } finally {
                  setLoading(false);
                }
              }}
            ></Checkbox>
            <Text ml="8px">
              {t('globalSetting.exchangeInline.trustScan.content')}
            </Text>
            <Info
              iconStyle={{ ml: '2x' }}
              content={t('globalSetting.exchangeInline.trustScan.tooltip')}
            />
          </Flex>
        </Box>
      ) : null}
      {!isShowMultiService && !loading ? (
        <GrantedAccess
          selectedPolicyType={selectedPolicyType}
          orgId={orgId}
        ></GrantedAccess>
      ) : null}
    </Box>
  );
});
export default InlineProtectionSettingsForExchangeOnline;
